import React, { useState } from 'react'
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Stack, TextField, Typography } from '@mui/material'
import useAccount from '../../hooks/useAccount'
import type { LoginResponse } from '../../types/app'
import {
  confirmLoginEnrol,
  isChallengeExpired,
  mfaErrorMessage,
  startLoginEnrol,
  type MfaSecret,
} from '../../services/mfaApi'
import AuthenticatorSetup from './AuthenticatorSetup'
import RecoveryCodesList from './RecoveryCodesList'

interface TwoStepLoginStepProps {
  /** The account that typed its password. */
  name: string
  challenge: string
  /** The account must set up its app first (enforced, not set up). */
  enrol: boolean
  /** Back to the password, with a reason when the sign-in expired. */
  onCancel: (reason?: string) => void
}

/**
 * The login page's second step (#1724): the code from the authenticator app
 * (or a recovery code), and, when two-step login is required of an account
 * that has not set it up, setting up the app first.
 */
const TwoStepLoginStep: React.FC<TwoStepLoginStepProps> = ({ name, challenge, enrol, onCancel }) => {
  const account = useAccount()
  const [code, setCode] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [secret, setSecret] = useState<MfaSecret>()
  const [finished, setFinished] = useState<{ response: LoginResponse, codes: string[] }>()
  const [useRecovery, setUseRecovery] = useState(false)

  const fail = (e: unknown) => {
    if (isChallengeExpired(e)) {
      onCancel('The sign-in expired. Enter your password again.')
      return
    }
    setError(mfaErrorMessage(e))
  }

  const submitCode = async () => {
    setError('')
    setBusy(true)
    try {
      await account.onLoginCode(challenge, code, remember, name)
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const beginSetup = async () => {
    setError('')
    setBusy(true)
    try {
      setSecret(await startLoginEnrol(challenge))
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const confirmSetup = async () => {
    setError('')
    setBusy(true)
    try {
      const { recovery_codes, ...response } = await confirmLoginEnrol(challenge, code, remember)
      setFinished({ response: response as LoginResponse, codes: recovery_codes })
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const codeField = (label: string, onSubmit: () => void) => (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
      <TextField
        fullWidth
        autoFocus
        label={label}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        margin="normal"
        required
        autoComplete="one-time-code"
        inputProps={useRecovery ? { 'aria-label': label } : { inputMode: 'numeric', 'aria-label': label }}
        InputLabelProps={{ shrink: true }}
      />
      <FormControlLabel
        control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
        label="Remember this browser for 30 days"
      />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Button fullWidth type="submit" variant="contained" color="info" sx={{ mt: 2, minHeight: '40px' }} disabled={busy || !code.trim()}>
        Verify
      </Button>
    </Box>
  )

  const back = (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <Link component="button" variant="body2" onClick={() => onCancel()} sx={{ color: 'info.main' }}>
        Back to sign in
      </Link>
    </Box>
  )

  if (finished) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">Two-step login is set up</Typography>
        <RecoveryCodesList codes={finished.codes} account={name} />
        <Button variant="contained" color="info" onClick={() => account.onCompleteLogin(finished.response, name)}>
          I have saved my codes, continue
        </Button>
      </Stack>
    )
  }

  if (enrol) {
    return (
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>Set up two-step login</Typography>
        {!secret ? (
          <Stack spacing={2}>
            <Typography variant="body2">
              Platinum now asks for a code from an authenticator app on your phone each time you sign in, as well as your password. Setting it up takes a minute.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Button variant="contained" color="info" onClick={beginSetup} disabled={busy}>Set up now</Button>
          </Stack>
        ) : (
          <>
            <AuthenticatorSetup secret={secret} />
            {codeField('Code from your app', confirmSetup)}
          </>
        )}
        {back}
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>Two-step login</Typography>
      <Typography variant="body2">
        {useRecovery
          ? 'Enter one of your recovery codes. Each code works once.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </Typography>
      {codeField(useRecovery ? 'Recovery code' : 'Code from your app', submitCode)}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Link component="button" variant="body2" sx={{ color: 'info.main' }} onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError('') }}>
          {useRecovery ? 'Use a code from my app' : 'Use a recovery code'}
        </Link>
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, textAlign: 'center' }}>
        Lost your phone and your recovery codes? Ask a Manager to reset your two-step login.
      </Typography>
      {back}
    </Box>
  )
}

export default TwoStepLoginStep
